import { createTheme } from '@mui/material/styles';
import * as varColor from 'app/styles/colors';

declare module '@mui/material/styles' {
  interface Palette {
    neutral: Palette['primary'];
    active: Palette['primary'];
  }

  interface TypographyVariants {
    body3: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    body3?: React.CSSProperties;
  }

  interface PaletteOptions {
    neutral: PaletteOptions['primary'];
    active: PaletteOptions['primary'];
  }

  interface TypeText {
    light: string;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
    active: true;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: varColor.lightGreen,
      contrastText: varColor.white,
    },
    error: {
      main: varColor.red,
    },
    text: {
      light: varColor.lightBlack,
      secondary: varColor.grey8,
    },
    neutral: {
      main: varColor.lightGrey,
      contrastText: varColor.blackGrey,
      dark: varColor.grey,
    },
    active: {
      main: 'rgba(33, 150, 243, 0.2)',
      contrastText: varColor.white,
    },
    background: { default: '#FAFAFA', paper: '#FFFFFF' },
  },
  typography: {
    fontFamily: ['Roboto', 'sans-serif'].join(','),
    subtitle1: {
      fontSize: 14,
      fontWeight: 700,
      letterSpacing: '0.17px',
    },
    subtitle2: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.4px',
    },
    body2: {
      fontSize: 14,
      fontWeight: 400,
      letterSpacing: '0.17px',
    },
    body3: {
      fontSize: 10,
    },
    caption: {
      fontSize: 12,
      lineHeight: '20px',
      letterSpacing: '0.25px',
    },
    button: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: '100%',
      letterSpacing: '0%',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: () => ({
        '::-webkit-scrollbar-thumb': {
          backgroundColor: '#BFBFC3',
          borderRadius: '4px',
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#aaaaae',
          cursor: 'pointer',
        },
        '::-webkit-scrollbar': {
          width: '8px',
          height: '8px',
        },
        '::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        body: {
          overflow: 'hidden',
          backgroundColor: varColor.white,
          fontSize: 12,
          fontWeight: 400,
          lineHeight: '16px',
          color: varColor.text,
        },
        p: {
          margin: 0,
        },
      }),
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: 10,
          lineHeight: '13px',
          minHeight: 13,
          fontWeight: 400,
          margin: '0 0 7px',
          marginTop: 3,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          border: '1px solid white',
          '&.active': {
            border: '1px solid black',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: 110,
          height: '36px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: varColor.lightGreen,
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '16px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#2196F3',
          },
          '&.Mui-disabled': {
            color: '#aad4e7',
          },
        },
      },
    },
  },
});
